// ITER221 — Apify Enrichment + Provider Status panel
//
// Drop-in card that surfaces:
//   • Provider status (Apify live / stub mode, last sync, success rate)
//   • Spend this month vs soft cap
//   • Cache hit %
//   • Restaurants enriched
//   • One-click "Enrich Tier 1 Top 5" + "Enrich All Qualifying"
//
// Works fully in stub mode — no Apify token required.

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Activity, AlertTriangle, CheckCircle2,
  PlayCircle, Sparkles, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Pill = ({ tone = 'slate', children, testid }) => {
  const tones = {
    slate:  'bg-slate-100 text-slate-700 border-slate-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    rose:   'bg-rose-50 text-rose-700 border-rose-200',
    sky:    'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="flex flex-col gap-1" data-testid={testid}>
    <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-base font-semibold text-slate-900">{value}</span>
    {sub && <span className="text-[11px] text-slate-500">{sub}</span>}
  </div>
);

const moneyFmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pctFmt = (n) => `${Math.round(Number(n) || 0)}%`;

export const ApifyEnrichmentPanel = ({ projectId, headers }) => {
  const [status, setStatus]       = useState(null);
  const [enrichment, setEnrichment] = useState(null);
  const [intel, setIntel]         = useState(null);
  const [audit, setAudit]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // ── Initial status load
  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/expansion-intelligence/apify/status`,
        { headers },
      );
      setStatus(r.data?.data || null);
    } catch (e) {
      console.warn('[apify-panel] status load failed', e);
    }
    if (projectId) {
      try {
        const s = await axios.get(
          `${API}/api/expansion-intelligence/enrich/${projectId}/status`,
          { headers },
        );
        setEnrichment(s.data?.data?.enrichment || null);
        setIntel(s.data?.data?.intel || null);
      } catch (e) {
        // 404 / no-snapshot is fine; the panel renders empty state.
      }
    }
  }, [projectId, headers]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // ── Enrich action — iter225: accept optional N
  const runEnrich = useCallback(async (scope, topN) => {
    if (!projectId) {
      setError('Save the project first to enable enrichment.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/expansion-intelligence/enrich/${projectId}`,
        { scope, top_n: topN ?? 10 },
        { headers },
      );
      setEnrichment(r.data?.data || null);
      setIntel(r.data?.intel || null);
      setAudit(r.data?.catchment_audit || null);
      await loadStatus();
    } catch (e) {
      const d = e?.response?.data;
      setError(typeof d === 'string' ? d : (d?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [projectId, headers, loadStatus]);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customN, setCustomN] = useState(10);

  const isLive   = !!status?.is_live;
  const cap      = Number(status?.soft_cap_usd || 5);
  const spent    = Number(status?.spent_this_month || 0);
  const capUsed  = Math.min(100, (spent / Math.max(cap, 0.0001)) * 100);
  const totalCalls = status?.total_calls || 0;
  const cacheHits  = status?.cache_hits || 0;
  const cacheHitPct = status?.cache_hit_pct || 0;
  const blocked   = enrichment?.blocked_by_cap;

  const enriched = enrichment?.competitor_count || 0;
  const lastRun  = enrichment?.finished_at
    ? new Date(enrichment.finished_at).toLocaleString()
    : '—';

  return (
    <Card className="border-slate-200" data-testid="apify-enrichment-panel">
      <CardContent className="p-5 space-y-5">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-900">
                Apify · Competitor Enrichment
              </h3>
              {isLive ? (
                <Pill tone="green" testid="apify-mode-pill">
                  <CheckCircle2 className="h-3 w-3" /> LIVE
                </Pill>
              ) : (
                <Pill tone="amber" testid="apify-mode-pill">
                  <Sparkles className="h-3 w-3" /> Stub mode
                </Pill>
              )}
            </div>
            <p className="text-xs text-slate-500 max-w-xl">
              Zomato &amp; Swiggy menu / price / daypart enrichment for the Top 10
              strategic competitors (configurable). Stub mode runs end-to-end
              without an API token — drop <code className="bg-slate-100 rounded px-1">APIFY_API_TOKEN</code>
              {' '}into the backend env to go live.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end shrink-0">
            <Button
              data-testid="apify-enrich-top10-btn"
              onClick={() => runEnrich('top_n', showAdvanced ? customN : 10)}
              disabled={loading || !projectId}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <PlayCircle className="h-4 w-4 mr-1.5" />
              {loading ? 'Enriching…' : `Enrich Top ${showAdvanced ? customN : 10}`}
            </Button>
            <Button
              data-testid="apify-enrich-all-btn"
              onClick={() => runEnrich('all_qualifying')}
              disabled={loading || !projectId}
              variant="outline"
              className="text-xs"
            >
              Enrich All Qualifying
            </Button>
            <button
              data-testid="apify-advanced-toggle"
              onClick={() => setShowAdvanced(s => !s)}
              className="text-[10px] text-slate-500 hover:text-slate-700 underline">
              {showAdvanced ? 'Hide advanced' : 'Advanced ▾'}
            </button>
            {showAdvanced && (
              <div className="flex items-center gap-2 text-xs"
                   data-testid="apify-advanced-panel">
                <label className="text-slate-600">Top N:</label>
                <input
                  type="number"
                  min={1}
                  max={25}
                  value={customN}
                  onChange={(e) => setCustomN(
                    Math.max(1, Math.min(25, parseInt(e.target.value || '10', 10)))
                  )}
                  className="w-16 border border-slate-300 rounded px-1.5 py-0.5"
                  data-testid="apify-topn-input"
                />
                <span className="text-[10px] text-slate-500">(1 – 25)</span>
              </div>
            )}
            {customN > 10 && showAdvanced && (
              <div className="text-[10px] text-amber-700 max-w-[200px] text-right"
                   data-testid="apify-cost-warning">
                Heads up — enriching {customN} competitors will use roughly
                ${(customN * 0.15).toFixed(2)} of your monthly cap.
              </div>
            )}
          </div>
        </div>

        {/* ── Catchment scope banner ─────────────────────────────── */}
        {audit && (
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-md px-3 py-2 text-xs text-emerald-900 flex flex-wrap items-center gap-x-4 gap-y-1"
                data-testid="apify-catchment-audit">
            <span className="font-semibold uppercase text-[10px] tracking-wide text-emerald-700">
              Catchment-scoped
            </span>
            <span>City: <b>{audit.city}</b></span>
            <span>F&B radius: <b>{audit.radius_m}m</b></span>
            <span>Qualifying competitors: <b>{audit.qualifying_competitor_count}</b></span>
            <span>Within catchment: <b>{audit.catchment_restaurant_count}</b></span>
            <span className="text-emerald-700">
              (Zomato {audit.catchment_by_platform?.zomato ?? 0} · Swiggy {audit.catchment_by_platform?.swiggy ?? 0})
            </span>
          </div>
        )}

        {/* ── Stats row ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 border-t border-slate-200 pt-4">
          <Stat
            testid="apify-stat-last-sync"
            label="Last sync"
            value={lastRun}
            sub={enrichment?.competitor_count
              ? `${enrichment.competitor_count} requests this run`
              : 'No enrichment yet'}
          />
          <Stat
            testid="apify-stat-restaurants"
            label="Acquired (city bulk)"
            value={enriched}
            sub={enrichment?.competitor_count
              ? `Apify cache items · post-catchment filter applied below`
              : '—'}
          />
          <Stat
            testid="apify-stat-cache-hit"
            label="Cache hit %"
            value={pctFmt(cacheHitPct)}
            sub={`${cacheHits} hits / ${totalCalls} calls`}
          />
          <Stat
            testid="apify-stat-spend"
            label="Spend this month"
            value={moneyFmt(spent)}
            sub={`Cap ${moneyFmt(cap)}`}
          />
          <Stat
            testid="apify-stat-calls"
            label="Apify calls (mo)"
            value={totalCalls}
            sub={`Zomato ${status?.calls?.zomato || 0} · Swiggy ${status?.calls?.swiggy || 0}`}
          />
        </div>

        {/* ── Cap meter ────────────────────────────────────────────── */}
        <div data-testid="apify-cap-meter">
          <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
            <span>Soft-cap usage</span>
            <span>{moneyFmt(spent)} / {moneyFmt(cap)} · {pctFmt(capUsed)}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-2 rounded-full ${capUsed > 95 ? 'bg-rose-500' : capUsed > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(capUsed, 100)}%` }}
            />
          </div>
          {blocked && (
            <div className="mt-2 text-[12px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Monthly cap reached — further enrichment blocked until next billing month.
            </div>
          )}
        </div>

        {/* iter224 P0-2 — Apify is a DATA ACQUISITION LAYER ONLY.
            Business conclusions (category gap, whitespace plays, daypart
            gap, etc.) are now rendered inside the Competitor Intelligence
            UI and the AI/PDF layers — not here. This panel is restricted
            to diagnostic + run telemetry. */}

        {/* ── Provider lineup (read-only) ──────────────────────────── */}
        <details className="text-xs text-slate-600" data-testid="apify-providers">
          <summary className="cursor-pointer select-none flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            <span>Provider actors</span>
          </summary>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 pl-5">
            {Object.entries(status?.providers || {}).map(([platform, info]) => (
              <div key={platform} className="rounded border border-slate-200 px-2.5 py-1.5 bg-white">
                <div className="font-medium text-slate-800 capitalize">{platform}</div>
                <div className="text-[11px] text-slate-500">Primary: <code>{info.primary}</code></div>
                <div className="text-[11px] text-slate-500">Fallback: <code>{info.fallback}</code></div>
              </div>
            ))}
          </div>
        </details>

        {error && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5"
                data-testid="apify-error">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApifyEnrichmentPanel;
